import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    GetSmsOptOutStatus200Response,
    NotificationPreferenceUpdateRequest,
    UsersService,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { finalize } from 'rxjs/operators';

interface PrefChannels {
    email: boolean;
    SMS: boolean;
}

interface NotificationPrefRow {
    preferenceKey?: string;
    title: string;
    description: string;
    channels?: PrefChannels;
    all: boolean;
    isAdmin: boolean;
}

function toUpdateRequest(
    preference: NotificationPrefRow,
): NotificationPreferenceUpdateRequest {
    return {
        preferenceKey: preference.preferenceKey!,
        channels: preference.channels
            ? { email: preference.channels.email, SMS: preference.channels.SMS }
            : undefined,
        isAdmin: preference.isAdmin,
        title: preference.title,
        description: preference.description,
        all: preference.all,
    };
}

@Component({
    selector: 'vb-user-notifications',
    templateUrl: './user-notifications.component.html',
    styleUrls: ['./user-notifications.component.scss'],
})
export class UserNotificationsComponent implements OnInit {
    isLoading = false;
    notificationPreferences: NotificationPrefRow[] | undefined;
    isDirty = false;
    smsOptedOut = false;
    private successId = 0;
    userPreferences: NotificationPrefRow[] = [];
    adminPreferences: NotificationPrefRow[] = [];

    constructor(
        private userService: UsersService,
        private translate: TranslateService,
        private snackbarService: VbSnackbarService,
    ) {}

    async ngOnInit(): Promise<void> {
        this.getSmsOptOutStatus();
        await this.getAllNotificationPreferences();
    }

    getSmsOptOutStatus(): void {
        this.userService.getSmsOptOutStatus().subscribe({
            next: (response: GetSmsOptOutStatus200Response) => {
                this.smsOptedOut = response.data?.optedOut === true;
            },
            error: () => {
                this.smsOptedOut = false;
            },
        });
    }

    getAllNotificationPreferences(): void {
        this.isLoading = true;

        this.userService
            .getPreferencesByAccountMappingsId()
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (response: any) => {
                    const preferences: NotificationPrefRow[] =
                        response.data?.map((preference: any) => ({
                            ...preference,
                            all:
                                preference.channels.email &&
                                preference.channels.SMS,
                        })) || [];

                    const [userPrefs, adminPrefs]: [
                        NotificationPrefRow[],
                        NotificationPrefRow[],
                    ] = [
                        preferences.filter(
                            (pref: NotificationPrefRow) => !pref.isAdmin,
                        ),
                        preferences.filter(
                            (pref: NotificationPrefRow) => pref.isAdmin,
                        ),
                    ];

                    const allUserSelected: boolean = userPrefs.every(
                        (pref: NotificationPrefRow) => pref.all,
                    );
                    const allAdminSelected: boolean = adminPrefs.every(
                        (pref: NotificationPrefRow) => pref.all,
                    );

                    // Synthetic master rows (index 0) — presentation only,
                    // sliced off before save.
                    this.userPreferences = [
                        {
                            title: 'All notifications',
                            description:
                                'Recommended — get every update by email and text.',
                            channels: undefined,
                            all: allUserSelected,
                            isAdmin: false,
                        },
                        ...userPrefs,
                    ];

                    this.adminPreferences = adminPrefs.length
                        ? [
                              {
                                  title: 'All notifications',
                                  description:
                                      'Recommended — get every admin update by email and text.',
                                  channels: undefined,
                                  all: allAdminSelected,
                                  isAdmin: true,
                              },
                              ...adminPrefs,
                          ]
                        : [];

                    this.notificationPreferences = [...preferences];
                },
                error: () => {
                    this.notificationPreferences = [];
                    this.userPreferences = [];
                    this.adminPreferences = [];
                    this.snackbarService.error(
                        this.translate.instant(
                            'Users.fetchNotificationPreferencesError',
                        ),
                    );
                },
            });
    }

    updateNotificationPreferences(): void {
        this.isLoading = true;

        const request: NotificationPreferenceUpdateRequest[] = [
            ...this.userPreferences.slice(1),
            ...this.adminPreferences.slice(1),
        ].map(toUpdateRequest);

        this.userService
            .updateNotificationPreferencesByAccountMappingsId(request)
            .subscribe({
                next: () => {
                    this.isDirty = false;
                    this.snackbarService.dismiss(this.successId);
                    this.successId = this.snackbarService.success(
                        this.translate.instant(
                            'Users.updateNotificationPreferencesSuccess',
                        ),
                    );
                    this.getAllNotificationPreferences();
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                    this.snackbarService.error(
                        this.translate.instant(
                            'Users.updateNotificationPreferencesError',
                        ),
                    );
                },
            });
    }

    /**
     * Channel checkbox flip. Same mutation as before (vb-pref-row emits the
     * next boolean directly, where the old markup passed a DOM event): set
     * the channel, re-derive the row's `all` (email && SMS — this exact
     * value ships in the save payload) and the master row.
     */
    onChannelSwitchChange(
        type: 'user' | 'admin',
        checked: boolean,
        index: number,
        channel: 'email' | 'SMS',
    ): void {
        if (channel === 'SMS' && this.smsOptedOut) return;
        const prefs: NotificationPrefRow[] =
            type === 'user' ? this.userPreferences : this.adminPreferences;

        const channels: PrefChannels | undefined = prefs[index].channels;
        if (!channels) return; // master row carries no channels
        channels[channel] = checked;
        prefs[index].all = channels.email && channels.SMS;
        prefs[0].all = prefs
            .slice(1)
            .every((pref: NotificationPrefRow) => pref.all);

        this.isDirty = true;
    }

    get isAllUserSelected(): boolean {
        return this.userPreferences[0].all;
    }

    get isAllAdminSelected(): boolean {
        return this.adminPreferences[0].all;
    }

    /**
     * Row toggle flip. Index 0 is the master row: it sets every type's `all`
     * and every channel. Any other index sets that type's two channels and
     * re-derives the master — identical mutation to the old switch handler.
     */
    onAllSwitchChange(
        type: 'user' | 'admin',
        checked: boolean,
        index: number,
    ): void {
        const preferences: NotificationPrefRow[] =
            type === 'user' ? this.userPreferences : this.adminPreferences;

        if (index === 0) {
            preferences.forEach((preference: NotificationPrefRow) => {
                preference.all = checked;
                if (preference.channels) {
                    this.setChannels(preference.channels, checked);
                }
            });
        } else {
            const channels: PrefChannels | undefined =
                preferences[index].channels;
            preferences[index].all = checked;
            if (channels) {
                this.setChannels(channels, checked);
            }
            preferences[0].all = preferences
                .slice(1)
                .every((pref: NotificationPrefRow) => pref.all);
        }

        this.isDirty = true;
    }

    /**
     * Display state of a type row's toggle: on while ANY channel is on.
     * (The payload's `all` flag keeps its historical email-AND-SMS meaning —
     * this is presentation only. Binding the toggle to `all` would render an
     * email-only preference as "off" with its checked box dimmed and
     * uneditable, locking users out of today's single-channel settings.)
     */
    isTypeOn(pref: NotificationPrefRow): boolean {
        return !!(
            pref.channels &&
            (pref.channels.email || (pref.channels.SMS && !this.smsOptedOut))
        );
    }

    private setChannels(channels: PrefChannels, checked: boolean): void {
        channels.email = checked;
        if (!this.smsOptedOut) channels.SMS = checked;
    }
}
